import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, RefreshCcw } from 'lucide-react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import {
  appQueryKeys,
  authenticateWithTelegram,
  linkTelegramIdentity,
  type AuthResponse,
} from '@/lib/app';
import { describeAppError } from '@/lib/api-error';
import { getSafeAppPath } from '@/lib/navigation';
import {
  extractTelegramLoginData,
  getTelegramLoginContext,
  hasTelegramLoginData,
} from '@/lib/telegram-login';
import { strings } from '@/lib/strings';

const POST_AUTH_REDIRECT_STORAGE_KEY = 'image2video-web-ui:post-auth-redirect:v1';

function getSafeReturnTo(searchParams: URLSearchParams): string {
  return getSafeAppPath(searchParams.get('return_to'));
}

function getPostAuthPath(returnTo: string, linkedIdentities: Array<{ type: string }>): string {
  const telegramLinked = linkedIdentities.some((identity) => identity.type === 'telegram');
  return telegramLinked ? returnTo : `/setup?return_to=${encodeURIComponent(returnTo)}`;
}

export function TelegramAuthCallback() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [started, setStarted] = useState(false);
  const telegramContext = getTelegramLoginContext();
  const intent = searchParams.get('intent') === 'link' ? 'link' : 'login';
  const returnTo = getSafeReturnTo(searchParams);
  const authData = useMemo(() => extractTelegramLoginData(searchParams), [searchParams]);

  const completeAuth = async (result: AuthResponse, nextPath: string) => {
    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem(POST_AUTH_REDIRECT_STORAGE_KEY, nextPath);
      } catch {
        // noop
      }
    }

    queryClient.setQueryData(appQueryKeys.session, {
      authenticated: true,
      account: result.account,
      linkedIdentities: result.linkedIdentities,
    });
    queryClient.setQueryData(appQueryKeys.profile, result.account);
    queryClient.setQueryData(appQueryKeys.identities, result.linkedIdentities);

    navigate(nextPath, { replace: true });
    await queryClient.invalidateQueries({ queryKey: ['app'] });
  };

  const loginMutation = useMutation({
    mutationFn: authenticateWithTelegram,
    onSuccess: async (result) => {
      await completeAuth(result, getPostAuthPath(returnTo, result.linkedIdentities));
    },
  });

  const linkMutation = useMutation({
    mutationFn: linkTelegramIdentity,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['app'] });
      navigate(returnTo, { replace: true });
    },
  });

  useEffect(() => {
    if (started) {
      return;
    }
    if (!telegramContext.available || !hasTelegramLoginData(searchParams)) {
      return;
    }

    setStarted(true);

    const payload = {
      authData,
      botId: telegramContext.botId,
      botUsername: telegramContext.botUsername,
    };

    if (intent === 'link') {
      void linkMutation.mutateAsync(payload).catch(() => undefined);
      return;
    }

    void loginMutation.mutateAsync(payload).catch(() => undefined);
  }, [authData, intent, linkMutation, loginMutation, searchParams, started, telegramContext.available, telegramContext.botId, telegramContext.botUsername]);

  if (!telegramContext.available) {
    return <Navigate to="/auth?mode=signin" replace />;
  }

  const error = loginMutation.error || linkMutation.error;
  const errorText = error ? describeAppError(error, strings.auth.telegramError) : null;
  const retryTelegram = () => {
    loginMutation.reset();
    linkMutation.reset();
    setStarted(false);
  };

  if (!hasTelegramLoginData(searchParams)) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <div className="surface-panel w-full max-w-md p-6 text-center">
          <div className="rounded-[1.2rem] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{strings.auth.telegramError}</div>
          <Link to="/auth?mode=signin" className="secondary-pill-button mt-4 w-full rounded-full">
            <ArrowLeft className="h-4 w-4" />
            {strings.auth.backToSignIn}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <div className="surface-panel w-full max-w-md p-6">
        {errorText ? (
          <div>
            <div className="rounded-[1.2rem] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{errorText}</div>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <button type="button" className="primary-pill-button rounded-full" onClick={retryTelegram}>
                <RefreshCcw className="h-4 w-4" />
                {strings.auth.telegramRetry}
              </button>
              <Link to="/auth?mode=signin" className="secondary-pill-button rounded-full">
                <ArrowLeft className="h-4 w-4" />
                {strings.auth.backToSignIn}
              </Link>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {strings.auth.telegramCompleting}
          </div>
        )}
      </div>
    </div>
  );
}
